import AppLayout from '../../components/layouts/AppLayout.js'
import Image from 'next/future/image'
import { GET_COMPANY_BY_SLUG } from '../../services/graphql/queries/getCompany.js'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head.js'
import { withToken } from '../../services/client.js'
import { GET_COMPANY_PRODUCTS } from '../../services/graphql/queries/getCompanyProducts'
import ContactForm from '../../components/seller/ContactForm'
import Error from 'next/error'
import SellerHeader from '../../components/seller/SellerHeader'
import Plugin from '../../components/Plugin'

export default function Manufacturer ({ company, error, companyProducts }) {
  const { t } = useTranslation(['seller', 'categories', 'services'])

  // As the company has been fetched beforehand, this HAS to be available here, if we found it
  if (!company?.id) {
    // Generic error page from Next.js
    return <Error statusCode={404} />
  }

  const products = companyProducts?.map(plugin => (
    <div
      key={plugin.id}
      className="col-12"
    >
      <Plugin plugin={plugin}/>
    </div>
  ))

  return (
    <AppLayout>
      <Head>
        {
          error
            ? <title>{t('detail.meta.title', { name: null })}</title>
            : <title>{t('detail.meta.title', { name: company.name })}</title>
        }
        {
          company.logo && company.logo.url && (
            <meta
              property="og:image"
              content={company.logo.url}
            />
          )}
      </Head>
      <div className="appendix appendix--bottomright appendix--turquoise appendix--big">
        <div className="container py-5">
          {
            error
              ? <div className="container py-5">
                <div className="text-center">
                  <div className="p-4 p-md-5 bg-light row align-items-center rounded-3">
                    <strong className="pb-3">{t('detail.not_approved.title')}</strong>
                    <p className="m-0">{t('detail.not_approved.info')}</p>
                  </div>
                </div>
              </div>
              : <div>
                <SellerHeader company={company} />
                <div className="row justify-content-between py-4 py-lg-5">
                  <div className="col-12">
                    <div className="row justify-content-md-between">
                      <div className="col-12">
                        <h2 className="fs-4 fw-bold text-dark">
                          {t('detail.about_the_seller')} {company.name}
                        </h2>
                        <div className="row justify-content-between">
                          <div className="col-9">
                            {
                              company.descriptions[0] && (
                                <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                                  {company.descriptions[0].description}
                                </ReactMarkdown>
                              )
                            }
                            {!company.descriptions[0] && t('detail.no_description')}
                          </div>
                          <div className="col-3 d-flex flex-column">
                            {
                              company.terms_of_service && (
                                <a
                                  href={company.terms_of_service}
                                  className="pb-4 nav-link text-primary fs-5 text-start"
                                  target="_blank"
                                  download
                                  rel="noreferrer"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    height="16"
                                    width="16"
                                    className="me-2"
                                  >
                                    <path
                                      stroke="#0d6efd"
                                      d="M3.75 23.25H20.25C21.0784 23.25 21.75 22.5784 21.75 21.75V8.121C21.7499 7.72345 21.592 7.3422 21.311 7.061L15.439 1.189C15.1578 0.907982 14.7765 0.750085 14.379 0.75H3.75C2.933 0.75 2.25 1.433 2.25 2.25V21.75C2.25 22.567 2.933 23.25 3.75 23.25Z"
                                    ></path>
                                    <path
                                      stroke="#0d6efd"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M21.75 8.25H15.75C15.3522 8.25 14.9706 8.09196 14.6893 7.81066C14.408 7.52936 14.25 7.14782 14.25 6.75V0.75"
                                    ></path>
                                  </svg>
                                  {t('detail.tac')}
                                </a>
                              )
                            }
                            {
                              company.privacy_declaration && (
                                <a
                                  href={company.privacy_declaration}
                                  className="nav-link text-primary fs-5 text-start"
                                  target="_blank"
                                  download
                                  rel="noreferrer"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    height="16"
                                    width="16"
                                    className="me-2"
                                  >
                                    <path
                                      stroke="#0d6efd"
                                      d="M3.75 23.25H20.25C21.0784 23.25 21.75 22.5784 21.75 21.75V8.121C21.7499 7.72345 21.592 7.3422 21.311 7.061L15.439 1.189C15.1578 0.907982 14.7765 0.750085 14.379 0.75H3.75C2.933 0.75 2.25 1.433 2.25 2.25V21.75C2.25 22.567 2.933 23.25 3.75 23.25Z"
                                    ></path>
                                    <path
                                      stroke="#0d6efd"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M21.75 8.25H15.75C15.3522 8.25 14.9706 8.09196 14.6893 7.81066C14.408 7.52936 14.25 7.14782 14.25 6.75V0.75"
                                    ></path>
                                  </svg>
                                  {t('detail.privacy_declaration')}
                                </a>
                              )
                            }
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
                {company.owner.role === 'Distributor' && (
                  <div>
                    <Image
                      alt=""
                      width={18}
                      height={18}
                      src="/images/icons/mail.svg"
                      className="me-2"
                    />
                    <a href={`mailto:${company.email}`}>{company.email}</a>
                  </div>
                )}
                {(company.subscription_type !== 'NONE' && company.company_constraints.has_lead === true) && (
                  <div>
                    <hr />
                    <ContactForm
                      company={company}
                    />
                    <hr />
                  </div>
                )}
                {
                  companyProducts.length > 0 && (
                    <div
                      id="sellerPlugins"
                      className="row d-flex justify-content-center py-4 py-lg-5"
                    >
                      <div className="col-12">
                        <h2 className="fs-4 fw-bold text-dark mb-4 mb-lg-5">
                          {t('detail.plugins')}
                        </h2>
                        <div className="row g-4 g-lg-5">
                          {products}
                        </div>
                      </div>
                    </div>
                  )
                }
              </div>
          }
        </div>
      </div>
    </AppLayout>
  )
}

export async function getServerSideProps ({ locale, query, req }) {
  const client = withToken(req.cookies.token, locale)

  let company = []
  let companyProducts = []
  let error = false

  try {
    const { data } = await client.query({
      query: GET_COMPANY_BY_SLUG,
      variables: {
        slug: query.slug,
        language: locale.toUpperCase()
      }
    })

    companyProducts = await client.query({
      query: GET_COMPANY_PRODUCTS,
      variables: {
        id: data.companyBySlug.id,
        first: 9999,
        page: 1,
        whereConditions: {
          AND: [
            { column: 'IS_ACTIVE', operator: 'EQ', value: true },
            { column: 'IS_APPROVED', operator: 'EQ', value: true }
          ]
        }
      }
    })

    company = data.companyBySlug
    companyProducts = companyProducts.data.company.products.data
  } catch (e) {
    error = true
  }

  return {
    props: {
      company,
      error,
      companyProducts,
      ...(await serverSideTranslations(locale, ['seller', 'footer', 'header', 'languages', 'plugins', 'categories', 'common', 'applications', 'routes', 'services']))
    }
  }
}
